import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';
import PackedBagModal from '../components/PackedBagModal';
import { supabase } from '../supabaseClient';

const { FiPlus, FiBriefcase, FiShare2, FiTrash2, FiClock, FiCheckCircle, FiCopy, FiExternalLink } = FiIcons;

export default function PackedBags() {
  const { state, dispatch } = useFamilyContext();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBag, setSelectedBag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copyingId, setCopyingId] = useState(null);

  useEffect(() => {
    // In a real app, this would be fetched via context
    setLoading(false);
  }, []);

  const handleSave = async (bagData) => {
    try {
      if (selectedBag) {
        await dispatch({ type: 'UPDATE_PACKED_BAG', payload: { ...bagData, id: selectedBag.id } });
      } else {
        await dispatch({ type: 'ADD_PACKED_BAG', payload: bagData });
      }
      setIsModalOpen(false);
      setSelectedBag(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Tasche wirklich löschen?')) {
      await dispatch({ type: 'DELETE_PACKED_BAG', payload: id });
    }
  };

  const copyShareLink = (bag) => {
    const url = `${window.location.origin}/#/shared/bag/${bag.id}`;
    navigator.clipboard.writeText(url);
    setCopyingId(bag.id);
    setTimeout(() => setCopyingId(null), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gepackte Taschen</h1>
          <p className="text-gray-500 dark:text-gray-400">Vergessen Sie nie wieder etwas beim Abholen.</p>
        </div>
        <button
          onClick={() => { setSelectedBag(null); setIsModalOpen(true); }}
          className="bg-blue-600 text-white px-5 py-2.5 rounded-2xl font-bold flex items-center shadow-lg hover:bg-blue-700 transition-all active:scale-95"
        >
          <SafeIcon icon={FiPlus} className="mr-2" /> Tasche packen
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {state.packedBags?.map(bag => {
          const child = state.familyMembers.find(m => m.id === bag.child_id);
          const returnedCount = bag.items?.filter(i => i.is_returned).length || 0;
          const totalCount = bag.items?.length || 0;
          const isComplete = totalCount > 0 && returnedCount === totalCount;

          return (
            <motion.div
              layout
              key={bag.id}
              className="bg-white dark:bg-gray-800 rounded-3xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-inner"
                    style={{ backgroundColor: child?.color || '#3B82F6' }}
                  >
                    {child?.avatar || (child?.name ? child.name.charAt(0) : '?')}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">{child?.name || 'Unbekannt'}</h3>
                    <p className="text-sm text-gray-500 flex items-center">
                      <SafeIcon icon={FiClock} className="mr-1 w-3 h-3" />
                      {new Date(bag.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button 
                    onClick={() => copyShareLink(bag)}
                    className={`p-2 rounded-xl transition-all ${copyingId === bag.id ? 'bg-green-100 text-green-600' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:text-blue-600'}`}
                    title="Teilen"
                  >
                    <SafeIcon icon={copyingId === bag.id ? FiCheckCircle : FiShare2} className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => handleDelete(bag.id)}
                    className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-500 hover:text-red-500 rounded-xl transition-all"
                    title="Löschen"
                  >
                    <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <div className="bg-blue-50 dark:bg-blue-900/30 p-2 rounded-lg mr-3">
                    <SafeIcon icon={FiBriefcase} className="text-blue-600 w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-wider font-bold text-gray-400">Ziel</span>
                    <p className="font-medium">{bag.destination}</p>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-2xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold text-gray-500 uppercase">Rückreise-Check</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isComplete ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                      {returnedCount} / {totalCount}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${(returnedCount / totalCount) * 100}%` }}
                      className={`h-full ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex space-x-2">
                <button
                  onClick={() => { setSelectedBag(bag); setIsModalOpen(true); }}
                  className="flex-1 py-2 text-sm font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/20 rounded-xl hover:bg-blue-100 transition-colors"
                >
                  Bearbeiten
                </button>
                <a 
                  href={`#/shared/bag/${bag.id}`}
                  target="_blank"
                  className="p-2 text-gray-500 hover:text-blue-600 bg-gray-100 dark:bg-gray-700 rounded-xl transition-colors"
                >
                  <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
                </a>
              </div>
            </motion.div>
          );
        })}

        {(!state.packedBags || state.packedBags.length === 0) && (
          <div className="col-span-full py-20 text-center bg-white dark:bg-gray-800 rounded-3xl border border-dashed border-gray-300 dark:border-gray-600">
            <div className="bg-gray-100 dark:bg-gray-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiBriefcase} className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Keine aktiven Taschen</h3>
            <p className="text-gray-500">Packen Sie die erste Tasche für Ihr Kind.</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <PackedBagModal
            bag={selectedBag}
            onClose={() => { setIsModalOpen(false); setSelectedBag(null); }}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>
    </div>
  );
}